import { Chip, Stack, Theme, useTheme } from '@mui/joy';
import { Warning } from '@phosphor-icons/react';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';

import { ExternalPartStatusType } from 'parts/enums';
import { Color, Size, Variant } from 'theme/enums';

interface IProps {
  value: ExternalPartStatusType;
  displayWarning?: boolean;
}

export const ExternalPartStatus = ({ value, displayWarning = false }: IProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  if (!value || value === ExternalPartStatusType.ACTIVE) {
    return null;
  }

  return (
    <Stack sx={{ padding: '4px 0', gap: 0.5 }}>
      <Chip
        variant={Variant.SOFT}
        size={Size.SM}
        sx={{
          color: getColor(theme, value),
          backgroundColor: getBackgroundColor(theme, value),
          textWrap: 'balance',
          fontWeight: 600,
        }}
      >
        {getLabel(value)}
      </Chip>

      {displayWarning && value === ExternalPartStatusType.ABANDONED && (
        <Chip
          color={Color.WARNING}
          variant={Variant.SOFT}
          size={Size.SM}
          startDecorator={
            <Warning
              weight="bold"
              style={{ fontSize: 19, color: theme.palette.custom.warning.main }}
            />
          }
          sx={{
            gap: 0.5,
            borderRadius: 6,
            padding: '4px',
            backgroundColor: theme.palette.custom.warning.light,
            color: theme.palette.warning[700],
            textWrap: 'auto',
          }}
        >
          {t('warningPartNumberAbandoned')}
        </Chip>
      )}
    </Stack>
  );
};

const getColor = (theme: Theme, value: ExternalPartStatusType) => {
  switch (value) {
    case ExternalPartStatusType.ACTIVE:
      return theme.palette.custom.green;
    case ExternalPartStatusType.DEACTIVATED:
      return theme.palette.text.primary;
    case ExternalPartStatusType.DEPRECATED:
      return theme.palette.custom.brown.dark;
    case ExternalPartStatusType.ABANDONED:
      return theme.palette.warning[700];
    default:
      return theme.palette.custom.purple;
  }
};

const getBackgroundColor = (theme: Theme, value: ExternalPartStatusType) => {
  switch (value) {
    case ExternalPartStatusType.ACTIVE:
      return theme.palette.success[100];
    case ExternalPartStatusType.DEACTIVATED:
      return theme.palette.neutral[300];
    case ExternalPartStatusType.DEPRECATED:
      return theme.palette.custom.brown.light;
    case ExternalPartStatusType.ABANDONED:
      return theme.palette.warning[100];
    default:
      return theme.palette.custom.cloud;
  }
};

const getLabel = (value: ExternalPartStatusType) => {
  switch (value) {
    case ExternalPartStatusType.ACTIVE:
      return t('labelActive');
    case ExternalPartStatusType.DEACTIVATED:
      return t('labelDeactivated');
    case ExternalPartStatusType.DEPRECATED:
      return t('labelDeprecated');
    case ExternalPartStatusType.ABANDONED:
      return t('labelAbandoned');
    default:
      return value;
  }
};
